import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Eye,
  EyeOff,
  Mail,
  Phone,
  ArrowRight,
  Sparkles,
  Users,
  DollarSign,
} from "lucide-react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import authService from "../../components/services/AuthService";
import { setUser, setUserFromVerification } from "../../store/slices/authSlice";
import Images from "../../utils/Images";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginType, setLoginType] = useState("email");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);

  const getValidationSchema = () =>
    Yup.object({
      identifier:
        loginType === "email"
          ? Yup.string()
              .email("Please enter a valid email address")
              .required("Email address is required")
              .trim()
          : Yup.string()
              .test(
                "phone-format",
                "Invalid phone number format",
                function (value) {
                  if (!value) return false;

                  // Remove any spaces or special characters
                  const cleanValue = value.replace(/[\s\-()]/g, "");

                  // If starts with +233, should be exactly 13 characters (+233 + 9 digits)
                  if (cleanValue.startsWith("+233")) {
                    return (
                      cleanValue.length === 13 &&
                      /^\+233[789]\d{8}$/.test(cleanValue)
                    );
                  }

                  // If starts with 233, should be exactly 12 characters (233 + 9 digits)
                  if (cleanValue.startsWith("233")) {
                    return (
                      cleanValue.length === 12 &&
                      /^233[789]\d{8}$/.test(cleanValue)
                    );
                  }

                  // If starts with 0, should be exactly 10 characters (0 + 9 digits)
                  if (cleanValue.startsWith("0")) {
                    return (
                      cleanValue.length === 10 &&
                      /^0[789]\d{8}$/.test(cleanValue)
                    );
                  }

                  // If starts with 7, 8, or 9, should be exactly 9 characters
                  if (/^[789]/.test(cleanValue)) {
                    return (
                      cleanValue.length === 9 && /^[789]\d{8}$/.test(cleanValue)
                    );
                  }

                  return false;
                }
              )
              .required("Phone number is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    });

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const requestData = {
        emailOrPhone: values.identifier,
        password: values.password,
      };
      const response = await authService.Login(requestData);
      if (response.data?.status_code === "000") {
        toast.success(response.data?.reason);
        
        const userData = response?.data?.data;
        
        // Check if user is verified
        const isVerified = userData?.email_verified_at || userData?.phone_number_verified_at;
        
        if (isVerified) {
          // User is verified, set as authenticated and go to home page
          dispatch(setUser(userData));
          navigate("/");
        } else {
          // User is not verified, set as in verification process and go to verification page
          dispatch(setUserFromVerification(userData));
          navigate("/verify-email", {
            state: {
              email: userData?.email,
              userData: userData,
            },
          });
        }
      } else {
        toast.error(response.data?.reason || "Sorry, Something went wrong");
      }
    } catch (error) {
      toast.error(error?.response?.data?.reason || "Sorry, Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-screen w-full flex flex-col sm:flex-row bg-[#FEFEFE]">
      {/* Left Side */}
      <motion.div
        className="relative hidden w-full h-full sm:w-1/2 sm:flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Background animations */}
        <motion.div
          className="absolute w-[800px] h-[800px] bg-white/10 rounded-full blur-xl"
          initial={{ scale: 0, rotate: 0 }}
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Floating stats cards */}
        <motion.div
          className="absolute top-10 right-10 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">
              50K+ Promoters
            </span>
          </div>
        </motion.div>

        <motion.div
          className="absolute left-10 bottom-20 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">$2M+ Earned</span>
          </div>
        </motion.div>

        <motion.div
          className="absolute top-1/2 right-1/4 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">
              95% Success Rate
            </span>
          </div>
        </motion.div>

        {/* Main image container */}
        <motion.div
          className="relative z-10 w-4/5 h-4/5"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="overflow-hidden relative w-full h-full rounded-2xl shadow-2xl backdrop-blur-sm bg-white/10"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop&crop=center"
              alt="dealboxx-promoter-login"
              className="object-cover w-full h-full rounded-2xl transition-transform duration-700 hover:scale-110"
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6 }}
            />

            {/* Additional floating elements for promoter theme */}
            <motion.div
              className="absolute top-1/4 left-1/4 p-3 rounded-full shadow-lg backdrop-blur-sm bg-white/20"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
            >
              <DollarSign className="w-6 h-6 text-white" />
            </motion.div>

            <motion.div
              className="absolute right-1/4 bottom-1/3 p-3 rounded-full shadow-lg backdrop-blur-sm bg-white/20"
              initial={{ scale: 0, rotate: 180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 1.7, duration: 0.8 }}
            >
              <Users className="w-6 h-6 text-white" />
            </motion.div>

            {/* Content overlay */}
            <motion.div
              className="flex absolute inset-0 flex-col justify-end items-center pb-20 bg-gradient-to-b from-black/10 via-black/20 to-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <motion.div
                className="px-8 text-center"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
              >
                <motion.h2
                  className="mb-6 text-5xl font-bold text-white drop-shadow-lg"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  Promoter Portal
                </motion.h2>
                <motion.p
                  className="mx-auto mb-8 max-w-md text-xl text-white/90"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  Access your affiliate dashboard and start earning
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Right Side */}
      <motion.div
        className="overflow-y-auto px-8 pt-16 pb-8 w-full sm:w-1/2 sm:h-screen sm:px-16"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header with Logo and Sign Up Button */}
        <motion.div
          className="flex justify-between items-center mb-6 sm:mb-8"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src={Images.logo} 
              alt="Dealboxx-Affiliate-Logo" 
              className="h-8 sm:h-10 w-auto"
            />
          </Link>

          {/* Sign Up Button */}
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="flex-shrink-0"
          >
            <Link to="/register">
              <motion.button
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 20px 40px rgba(255, 108, 108, 0.3)"
                }}
                whileTap={{ scale: 0.95 }}
                className="group relative px-4 py-2 sm:px-6 sm:py-3 font-semibold text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] rounded-xl sm:rounded-2xl shadow-lg transition-all duration-300 overflow-hidden"
              >
                {/* Animated background effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-[#FF9B9B] to-[#FF6C6C] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "0%" }}
                  transition={{ duration: 0.3 }}
                />
                
                {/* Button content */}
                <div className="relative flex items-center gap-1 sm:gap-2">
                  <motion.span
                    className="text-xs sm:text-sm"
                    whileHover={{ x: 2 }}
                    transition={{ duration: 0.2 }}
                  >
                    Sign Up
                  </motion.span>
                  <motion.div
                    whileHover={{ x: 3, rotate: 15 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                  </motion.div>
                </div>

                {/* Sparkle effect */}
                <motion.div
                  className="absolute top-1 right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full opacity-0 group-hover:opacity-100"
                  animate={{ 
                    scale: [1, 1.5, 1],
                    opacity: [0, 1, 0]
                  }}
                  transition={{ 
                    duration: 1.5,
                    repeat: Infinity,
                    delay: 0.5
                  }}
                />
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>

        {/* Welcome Text */}
        <motion.div
          className="mb-6 space-y-2"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
            Welcome Back, Promoter!
          </h1>
          <p className="text-[#808080] text-base">
            Sign in to access your affiliate dashboard and continue earning
          </p>
        </motion.div>

        {/* Form */}
        <Formik
          initialValues={{ identifier: "", password: "" }}
          validationSchema={getValidationSchema()}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          validateOnChange={true}
        >
          {({ values, touched, errors, setFieldValue }) => (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <Form className="space-y-6">
                <div className="flex p-1 bg-gray-100 rounded-2xl">
                  <button
                    type="button"
                    onClick={() => {
                      if (loginType !== "email") {
                        setLoginType("email");
                        setFieldValue("identifier", "");
                      }
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl transition-all duration-300 ${
                      loginType === "email"
                        ? "bg-white text-gray-900 shadow-lg"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (loginType !== "phone") {
                        setLoginType("phone");
                        setFieldValue("identifier", "");
                      }
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl transition-all duration-300 ${
                      loginType === "phone"
                        ? "bg-white text-gray-900 shadow-lg"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Phone className="w-4 h-4" />
                    Phone
                  </button>
                </div>

                {/* Identifier Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {loginType === "email" ? "Email Address" : "Phone Number"}
                  </label>
                  <div className="relative">
                    <Field
                      name="identifier"
                      type={loginType === "email" ? "email" : "tel"}
                      placeholder={
                        loginType === "email"
                          ? "e.g., john.doe@example.com"
                          : "e.g., +233 24 123 4567 or 024 123 4567"
                      }
                      className="px-4 py-4 w-full placeholder-gray-400 text-gray-900 rounded-2xl border transition-all duration-300 bg-white border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent hover:border-gray-300"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {loginType === "email" ? (
                        <Mail className="w-5 h-5 text-gray-400" />
                      ) : (
                        <Phone className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  {loginType === "phone" && (
                    <div className="mt-1 text-xs text-gray-500">
                      Format: +233 XX XXX XXXX or 0XX XXX XXXX (Ghana numbers
                      only)
                    </div>
                  )}
                  {loginType === "email" && (
                    <div className="mt-1 text-xs text-gray-500">
                      Enter your registered email address
                    </div>
                  )}
                  <ErrorMessage
                    name="identifier"
                    component="div"
                    className="mt-1 text-sm text-red-500"
                  />
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="relative">
                    <Field
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      className="px-4 py-4 pr-12 w-full placeholder-gray-400 text-gray-900 rounded-2xl border transition-all duration-300 bg-white border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent hover:border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="mt-1 text-sm text-red-500"
                  />
                </div>

                {/* Forgot Password Link */}
                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#FF6C6C] transition-colors hover:text-[#ff8b8b] font-medium"
                  >
                    Forgot your password?
                  </Link>
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={loading || isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex gap-2 justify-center items-center px-6 py-4 w-full font-semibold text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(loading || isSubmitting) ? (
                    <>
                      <div className="w-5 h-5 rounded-full border-2 animate-spin border-white/30 border-t-white" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Access Dashboard
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </motion.button>
              </Form>
            </motion.div>
          )}
        </Formik>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-8 text-center"
        >
          <p className="text-sm text-gray-500">
            Already have an account?{" "}
            <Link
              to="/register"
              className="font-semibold text-[#FF6C6C] hover:text-[#ff8b8b] transition-colors duration-200"
            >
              Create New Account
            </Link>
          </p>
        </motion.div>

        {/* Feature Cards */}
        <motion.div
          className="grid grid-cols-3 gap-3 mt-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
        >
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">💰</div>
              <div className="text-xs text-gray-600">High Commissions</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">📊</div>
              <div className="text-xs text-gray-600">Real-time Analytics</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🚀</div>
              <div className="text-xs text-gray-600">Quick Payments</div>
            </motion.div>
          </div>
        </motion.div>

        {/* Promoter Benefits Section */}
        <motion.div
          className="mt-6 p-4 rounded-2xl bg-gradient-to-r from-[#FF6C6C]/10 to-[#FF9B9B]/10 border border-[#FF6C6C]/20"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          <div className="flex gap-2 items-center mb-2">
            <Sparkles className="w-4 h-4 text-[#FF6C6C]" />
            <span className="text-sm font-semibold text-[#FF6C6C]">
              Why Choose Dealboxx?
            </span>
          </div>
          <div className="space-y-1 text-xs text-gray-600">
            <div>• Instant commission tracking & withdrawals</div>
            <div>• Exclusive deals with highest payouts</div>
            <div>• 24/7 support & promotional tools</div>
          </div>
        </motion.div>

        {/* Back to Landing */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-8 text-center"
        >
          <Link
            to="/"
            className="text-sm text-gray-500 transition-colors hover:text-[#FF6C6C]"
          >
            ← Back to Home
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;
